import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import routeTyrePage from '../../pages/TyrePage/route';
import { getInquiryConfig } from '../../lib/inquiryConfig';
import { tyrePropType } from './propTypes';
import TyreInfo from './TyreInfo';
import PriceActions, { ComparisonBadge } from './PriceActions';
import ExpandedDetails from './ExpandedDetails';
import {
  Card,
  CardMain,
  LeftColumn,
  CenterColumn,
  RightColumn,
  ExpandButton,
  BrandLogo,
} from './SearchResultCard.style';

const getBrandLogoUrl = (brand) => {
  if (!brand?.name) return null;
  try {
    return require(`../../assets/images/${brand.name.toLowerCase()}-tyre.png`);
  } catch {
    return null;
  }
};

const SearchResultCard = ({
  tyre,
  isOpen,
  onToggle,
  quantity,
  onQuantityChange,
  onAddToCart,
  isAddingToCart,
  onDeliveryDateChange,
  onRefreshDelivery,
  isInComparison,
  onToggleCompare,
  isComparisonFull,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const handleIncrement = () => onQuantityChange(tyre._id, quantity + 1);
  const handleDecrement = () => onQuantityChange(tyre._id, Math.max(1, quantity - 1));

  const handleInquiry = () => {
    const config = getInquiryConfig(i18n.language);
    const ccParam = config.cc.length > 0 ? `?cc=${config.cc.join(';')}` : '';
    const subject = encodeURIComponent(t('inquirySubject'));
    const body = t('inquiryBody', {
      catalogNumber: tyre.catalogNumber,
      design: tyre.design,
      name: tyre.name,
    }).replace(/\n/g, '%0D%0A');

    window.location.href = `mailto:${config.mailTo}${ccParam}${ccParam ? '&' : '?'}subject=${subject}&body=${body}`;
  };

  const handleDetailsClick = () => {
    history.push(routeTyrePage(tyre.ean));
  };

  return (
    <Card isInComparison={isInComparison}>
      {isInComparison && (
        <ComparisonBadge title={t('inComparison')}>
          <i className="mdi mdi-scale-balance" />
        </ComparisonBadge>
      )}

      <CardMain>
        <CenterColumn>
          {tyre.brand && getBrandLogoUrl(tyre.brand) && (
            <BrandLogo src={getBrandLogoUrl(tyre.brand)} alt={tyre.brand.name} />
          )}
        </CenterColumn>

        <LeftColumn>
          <TyreInfo tyre={tyre} />
        </LeftColumn>

        <RightColumn>
          <PriceActions
            tyre={tyre}
            quantity={quantity}
            onIncrement={handleIncrement}
            onDecrement={handleDecrement}
            onAddToCart={onAddToCart}
            isAddingToCart={isAddingToCart}
            onInquiry={handleInquiry}
            onDetails={handleDetailsClick}
            isInComparison={isInComparison}
            onToggleCompare={onToggleCompare}
            isComparisonFull={isComparisonFull}
          />
        </RightColumn>
      </CardMain>

      <ExpandButton onClick={() => onToggle(tyre._id)} isOpen={isOpen}>
        {isOpen ? t('hideDetails') : t('showDetails')}
        <i className="mdi mdi-chevron-down" />
      </ExpandButton>

      <ExpandedDetails
        isOpen={isOpen}
        tyre={tyre}
        onDeliveryDateChange={onDeliveryDateChange}
        onRefreshDelivery={onRefreshDelivery}
        onViewDetails={handleDetailsClick}
      />
    </Card>
  );
};

SearchResultCard.propTypes = {
  tyre: tyrePropType.isRequired,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  quantity: PropTypes.number,
  onQuantityChange: PropTypes.func.isRequired,
  onAddToCart: PropTypes.func.isRequired,
  isAddingToCart: PropTypes.bool,
  onDeliveryDateChange: PropTypes.func,
  onRefreshDelivery: PropTypes.func,
  isInComparison: PropTypes.bool,
  onToggleCompare: PropTypes.func,
  isComparisonFull: PropTypes.bool,
};

export default SearchResultCard;
